import { Component, OnInit } from "@angular/core";
import { BsModalService } from "ngx-bootstrap/modal";
import { TypeaheadMatch, TypeaheadModule } from "ngx-bootstrap/typeahead";
import { HotToastService } from "@ngneat/hot-toast";
import { environment } from "src/environments/environment";
import {
  XeroConnectedWorkspace,
  XeroIntegrationResponse,
  XeroIntegrationService,
} from "../../xero-integration.service";
import { AccountantService } from "./accountant.service";
import { DeleteConfirmationModalComponent } from "./modals/delete-confirmation-modal.component";
import { XeroAccountsModalComponent } from "./xero-accounts-modal/xero-accounts-modal.component";
import { ActiveClientsOnlyPipe } from "../common/active-clients-only.pipe";
import { FormsModule } from "@angular/forms";
import { NgIf, NgFor } from "@angular/common";

export const IntegrationSteps = {
  WorkspacePicker: 1,
  WorkspaceTable: 2,
};

@Component({
    selector: "app-xero-integration",
    templateUrl: "./xero-integration.component.html",
    styleUrls: ["./xero-integration.component.scss"],
    imports: [
        NgIf,
        FormsModule,
        TypeaheadModule,
        NgFor,
        ActiveClientsOnlyPipe,
    ]
})
export class XeroIntegrationComponent implements OnInit {
  loading: boolean = false;
  connectedWorkspaces: XeroConnectedWorkspace[] = [];
  availableWorkspaces: any[] = [];
  selectedWorkspace: any = null;
  integrationStep = IntegrationSteps.WorkspaceTable;

  constructor(
    public toastr: HotToastService,
    private modalService: BsModalService,
    private xeroService: XeroIntegrationService,
    private accountantService: AccountantService
  ) {}

  ngOnInit(): void {
    this._getConnectedWorkspaces();
    this._getAvailableWorkspaces();
  }

  _getConnectedWorkspaces() {
    this.loading = true;
    this.xeroService
      .getConnectedWorkspaces()
      .subscribe((res: XeroConnectedWorkspace[]) => {
        this.connectedWorkspaces = res;
        this.integrationStep =
          this.connectedWorkspaces.length == 0
            ? IntegrationSteps.WorkspacePicker
            : IntegrationSteps.WorkspaceTable;
      })
      .add(() => (this.loading = false));
  }

  _getAvailableWorkspaces() {
    this.loading = true;
    this.accountantService
      .get_clients()
      .then(
        (res: any) => (this.availableWorkspaces = res),
        (err: XeroIntegrationResponse) => this._handleResponse(err, true)
      )
      .finally(() => (this.loading = false));
  }

  _handleResponse(res: XeroIntegrationResponse, error: boolean) {
    this.loading = false;
    let _toastr = error ? this.toastr.error : this.toastr.success;

    _toastr(res.message);
  }

  selectWorkspace(event: TypeaheadMatch) {
    this.connectedWorkspaces.push({
      uuid: null,
      company: event.item.id,
      company_name: event.item.name,
      tenant_name: null,
    } as XeroConnectedWorkspace);
    this.integrationStep = IntegrationSteps.WorkspaceTable;
  }

  launchAuthenticationForm(workspaceId: number) {
    this.loading = true;
    let isListenerExhausted = false;

    const path = `${environment.apiUrl}/integration/xero/auth?integrate=${workspaceId}`;

    let parameters = "location=1,width=800,height=650";
    parameters +=
      ",left=" + (screen.width - 800) / 2 + ",top=" + (screen.height - 650) / 2;

    const win = window.open(path, "connectPopup", parameters);
    window.addEventListener(
      "message",
      (e: MessageEvent) => {
        try {
          const response = JSON.parse(e.data);
          console.log(response);
          // TODO: refactor close.html to accept different response type
          if (response.type != "quickbooks" || isListenerExhausted) {
            console.log("not xero response or listener is exhausted");
            return;
          }
          isListenerExhausted = true;
          if (response.status === "success") {
            this.toastr.success(`Authenticated in Xero.`);
            this._getConnectedWorkspaces();
          } else {
            let errorMessage = response.message
              ? response.message
              : `Couldn't authenticate in Xero.`;
            this.toastr.error(errorMessage);
            console.error(errorMessage);
          }
          win.close();
        } catch {
          return;
        }
      },
      false
    );

    const timer = setInterval(() => {
      if (win.closed) {
        this.loading = false;
        clearInterval(timer);
      }
    }, 1000);
  }

  manageAccounts(connectionUuid: string) {
    this.modalService.show(XeroAccountsModalComponent, {
      class: "modal-md modal-white-bg",
      initialState: {
        connectionUuid,
      },
    });
  }

  disconnectWorkspace(connectionUuid: string) {
    this.loading = true;
    this.xeroService.disconnectWorkspace(connectionUuid).subscribe(
      (res: XeroIntegrationResponse) => {
        this._handleResponse(res, false);
        this._getConnectedWorkspaces();
      },
      (err: XeroIntegrationResponse) => this._handleResponse(err, true)
    );
  }

  disconnectWorkspaceConfirmationModal(
    connectionUuid: string,
    companyName: string
  ) {
    const initialState = {
      displayName: companyName,
    };

    const deleteModal = this.modalService.show(
      DeleteConfirmationModalComponent,
      { initialState }
    );
    deleteModal.content.onClose.subscribe((confirmed) => {
      if (confirmed) {
        this.disconnectWorkspace(connectionUuid);
      }
    });
  }
}
